import { FieldArray, getIn, useFormikContext } from "formik";
import { FormikDateSelector, FormikFileUpload, FormikSelect, FormikTextInput } from "./CustomFormikFormFields";

import * as Yup from 'yup'
import { FileUploaderResponse } from "./FileUploader";
import { AttachmentCard } from "../../modules/core/attachments/AttachmentCard";
import { cloneDeep, omit, pick, remove } from "lodash";
import { isPopulatedArray, isValidString } from "../helpers";
import { Fragment, useEffect, useRef } from "react";
import { BillingTransportTypes } from "../../modules/core/billing";
import { KTSVG } from "../../../_metronic/helpers";

import clsx from "clsx";

const transportTypes = [
  'Accomodations',
  'Air Ticket',
  'Client Pickup',  
  'Overseas Transport',
  'Local Transport',
  'Same as Application ID',  
];

const durationOptions = [
  'Full Day',
  'Half Day - AM',
  'Half Day - PM',
  'Sampling',
  'Overtime', // (Date and Time Range + Attachment
];


export type BillMode = 'YES' | 'NO' | 'OPTIONAL';

export interface IDLMBillingTransportDetails {
  billingRequired: BillMode,
  type: BillingTransportTypes
  transportDetails: string
  transportAttachments: Array<number>
}

export interface IDLMBillingDetails {
  billingRequired: BillMode
  inspectionDate: string
  transports: Array<IDLMBillingTransportDetails>  
  durationOption: string
  durationRange: string
  durationsAttachments: Array<number>
  hasTransports: boolean
}

export const initBillingTransportDetails = {
    type: '',
    transportDetails: '',
    transportAttachments: [],
}

const initBillingDetails = {
  billingRequired: 'NO',
  inspectionDate: '',
  transports: [
    {
      ...initBillingTransportDetails,
      billingRequired: 'NO',
    }
  ],
  durationOption: '',
  durationRange: '',
  durationsAttachments: [],
  hasTransports: false,
}

const BILLING_FIELDS = [
  'billingRequired',
  'inspectionDate',
  'transports',
  'durationOption',
  'durationRange',
  'durationsAttachments',
  'hasTransports',
];

const billingTransportSchema = Yup.object({
  billingRequired: Yup.string().trim(),
  type: Yup.string().trim().label('Transport Type')
  .when("billingRequired", {
    is: 'YES',        
    then: (schema) => schema.required(),
  }),
  transportDetails: Yup.string().trim().label('Transport Details')
  .when(['billingRequired','type'], {
    is: (billingRequired: BillMode, type: string) => ((billingRequired === 'YES' || billingRequired === 'OPTIONAL')  && isValidString(type) && (type !== 'Client Pickup')),
    then: (schema) => schema.required('Details required'),
  }),
  transportAttachments: Yup.array().label('Transport Attachments')
  .when(['billingRequired','type'], {
    is: (billingRequired: BillMode, type: string) => ((billingRequired === 'YES' || billingRequired === 'OPTIONAL')  && isValidString(type) && (! ['Client Pickup','Same as Application ID'].includes(type))),
    then: (schema) => schema.min(1, 'Transport Attachment(s) required').required()
  }),
})

const billingDetailsSchema = Yup.object({
  billingRequired: Yup.string().trim(),
  inspectionDate: Yup.string().trim().label('Inspection Date')
  .when(['billingRequired','durationOption','hasTransports'], {
    is: (billingRequired: BillMode, durationOption: string, hasTransports: boolean) => ((billingRequired === 'YES') || (billingRequired === 'OPTIONAL' && (isValidString(durationOption) || hasTransports))),
    then: (schema) => schema.required(),
  }),
  transports: Yup.array().of(billingTransportSchema).label('Transport Details')
  .when("billingRequired", {
    is: 'YES',        
    then: (schema) => schema.min(1, 'Transport Details').required('Add Transport Details'),
  }),
  durationOption: Yup.string().trim().label('Duration Details')
  .when("billingRequired", {
    is: 'YES',          
    then: (schema) => schema.required(),
  }),
  durationRange: Yup.string().trim().label('Duration Range')
    .when('durationOption', {
    is: 'Overtime',
    then: (schema) => schema.required('Range required'),
  }),
  durationsAttachments: Yup.array().label('Duration Attachments')
  .when('durationOption', {
    is:'Overtime',
    then: (schema) => schema.min(1, 'Duration attachment(s) required').required()
  }),
  hasTransports: Yup.boolean(),
});

const ParseBillingFields = (payload: any, values: any) => {

  // cleanup
  payload = omit(payload, BILLING_FIELDS);  

  const { 
    billingRequired, inspectionDate,    
    durationOption, durationRange, durationsAttachments,    
  } = values;

  if (billingRequired === 'NO') {
    return payload;
  }  

  // add inspection date
  if (isValidString(inspectionDate)) {
    Object.assign(payload, {inspectionDate});    
  }

  // add duration option
  if (isValidString(durationOption)) {
    let durationVals = [durationOption];
    if (durationOption === 'Overtime' && isValidString(durationRange)) durationVals.push(durationRange);
    
    Object.assign(payload, {duration: durationVals.join(' - ')});
  }

  if (isPopulatedArray(durationsAttachments)) {
    Object.assign(payload, { durationsAttachments : durationsAttachments.map((o : any) => o.id)})
  }  

  // clear empty values from transports
  let transports = cloneDeep(values.transports) as IDLMBillingTransportDetails[];
  remove(transports, (data: IDLMBillingTransportDetails) => (! isValidString(data.type)))
  if (isPopulatedArray(transports)) {
    Object.assign(payload, {
      transports: transports.map((obj:IDLMBillingTransportDetails) => {
        let vals = pick(obj, ['type','transportDetails']);
        if (isPopulatedArray(obj?.transportAttachments)) {
          Object.assign(vals, { transportAttachments : obj.transportAttachments.map((o : any) => o.id)})
        }
        return vals;
      }),
    });      
  }

  return payload;
}


const FormikBillingDetails = () => {

  // add validator delay
  const timerRef = useRef<any>();

  const { values, setFieldValue, validateField } = useFormikContext();
  
  const { billingRequired, transports, durationOption, hasTransports } = values as IDLMBillingDetails;

  const revalidateHasTransports = () => {
    let _currentValue = getIn(values, 'hasTransports');
    if (! (isPopulatedArray(transports))) {
      if (_currentValue) setFieldValue('hasTransports', false, true);
      return;
    }

    let hasTransports = false;
    transports.forEach((data:IDLMBillingTransportDetails, idx: number) => {      
      if (isValidString(data.type)) {
        hasTransports = true;
      }
    });
    if (_currentValue !== hasTransports) {
      setFieldValue('hasTransports', hasTransports, true);  
    }    
  }

  const resetTimeout = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined
    }
  }

  useEffect(() => {
    if (! (isPopulatedArray(transports))) {      
      return;
    }    
    transports.forEach((data:IDLMBillingTransportDetails, idx: number) => {
      if (data.billingRequired !== billingRequired) {
        setFieldValue(`transports.${idx}.billingRequired`, billingRequired);
      }              
    });    
  // eslint-disable-next-line
  }, [billingRequired]);


  useEffect(() => {
    resetTimeout();
    
    timerRef.current = setTimeout(() => {
      revalidateHasTransports();
    }, 800);

    return(() => {
      resetTimeout();
    })
  // eslint-disable-next-line
  }, [transports]);


  if (billingRequired === 'NO') {
    return null;
  }

  return (
  <div className="my-2">
    <div className="row">                
      <FormikDateSelector 
        size="6" 
        label="Inspection Date" 
        name="inspectionDate" 
        showShiftSelector={false} 
        dateFormat='LL' 
        required={((billingRequired === 'YES') || (billingRequired === 'OPTIONAL' && (isValidString(durationOption) || hasTransports)))} 
      />
    </div> 
    <div className="row">
      <FormikSelect 
        size="6" 
        label="Duration of Inspection" 
        name="durationOption" 
        options={durationOptions.map(str => ({value: str, text: str}))} 
        required={billingRequired === 'YES'} 
      />      
      { (durationOption && durationOption === 'Overtime') &&
      <FormikTextInput size="6" label="Duration Range" name="durationRange" required />
      }      
    </div>
    <div className="row">
      <FormikFileUpload size="6" subTitle="Click or drop here" label="Duration Attachments" name="durationsAttachments" acceptFiles="UserFileFormats" isFileArray required={(durationOption === 'Overtime')}/>
      <FieldArray
        name="durationsAttachments"
        render={arrayHelpers => (
          <Fragment>
          { /* @ts-ignore */}
          { (values.durationsAttachments && values.durationsAttachments.length > 0) && values.durationsAttachments.map( (o: FileUploaderResponse, idx: number) => (
            <div className="col-6" key={`durationsAttachments${idx}`}>
            <AttachmentCard                
              className="border py-1 justify-content-center"
              idx={idx}
              fileId={o.id}
              fileName={o.name}
              onRemove={(e: any) => {
                e.stopPropagation();
                const elem = e.target.closest('.attachment-card');
                if (elem) {
                  const {idx} = elem.dataset;
                  arrayHelpers.remove(idx);
                }
              }}
            />
            </div>
          ))
          }
          </Fragment>
        )}
      />
    </div>
    <FieldArray
      name="transports"
      render={rowHelpers => (
        <>
        <div className="row mt-2">
          <div className={clsx("col-4 fw-bold text-muted fs-7", {'required': billingRequired==='YES'})}>
            Transport Details
          </div>
          <div className="col-8 text-end">
            <div className="d-inline-block text-muted fs-7 me-2">Click to add →</div>
            <button
              type="button"
              className="siteBtn btn btn-light-success text-success btn-active-success px-2 py-1 lh-0 me-3"
              onClick={() => rowHelpers.push( { ...initBillingTransportDetails, billingRequired } )}              
              title="Click to Add Transport"                
            >
              <KTSVG path='/media/icons/duotune/arrows/arr017.svg' className='svg-icon m-0' />
            </button>                    
          </div>
        </div>
        <div className='separator border-gray-200 my-1'></div>
        <div className="scroll-y mh-300px -px-4">
        { (transports && transports.length > 0) && 
        transports.map((row: IDLMBillingTransportDetails, idx: number) => (
        <Fragment key={`transportRow${idx}`}>
          <div className="d-flex flex-row align-items-start gap-2">            
            <div className="flex-grow-1">
              <div className="row">
                <FormikSelect 
                  size="6" 
                  label="Transport Type" 
                  name={`transports.${idx}.type`} 
                  options={transportTypes.map(str => ({value: str, text: str}))} 
                  required={billingRequired === 'YES'} onChange={() => { validateField(`transports.${idx}`); validateField('transports'); }} 
                />
                {(row.type !== 'Client Pickup') && 
                <FormikTextInput 
                  size="6" 
                  label={row.type === 'Same as Application ID' ? 'Application ID' : 'Enter Details'} 
                  name={`transports.${idx}.transportDetails`} 
                  required={(billingRequired === 'YES' || billingRequired === 'OPTIONAL')  && isValidString(row.type)}  
                  onChange={() => { validateField(`transports.${idx}`); validateField('transports'); }} 
                />
                }
              </div>
              <div className="row">
                <FormikFileUpload 
                  size="6" 
                  subTitle="Click or drop here" 
                  label="Transport Attachments" 
                  name={`transports.${idx}.transportAttachments`} 
                  acceptFiles="UserFileFormats" 
                  isFileArray 
                  required={((billingRequired === 'YES' || billingRequired === 'OPTIONAL')  && isValidString(row.type) && (! ['Client Pickup','Same as Application ID'].includes(row.type)))} 
                  onUpdateFunc={() => { validateField(`transports.${idx}`); validateField('transports'); }}
                />
                <FieldArray
                  name={`transports.${idx}.transportAttachments`}
                  render={transportAttachmentHelpers => (
                  <Fragment>
                  { /* @ts-ignore */}
                  { (row.transportAttachments && row.transportAttachments.length > 0) && row.transportAttachments.map( (o: FileUploaderResponse, attachidx: number) => (
                  <div className="col-6" key={`transportAttachments${idx}-${attachidx}`}>
                    <AttachmentCard                
                      className="border py-1"
                      idx={attachidx}
                      fileId={o.id}
                      fileName={o.name}
                      onRemove={(e: any) => {
                        e.stopPropagation();
                        const elem = e.target.closest('.attachment-card');
                        if (elem) {
                          const {idx} = elem.dataset;
                          transportAttachmentHelpers.remove(idx);
                        }
                      }}
                    />
                  </div>
                  ))
                }
                </Fragment>
                )}
              />
            </div>            
          </div>  
          {(transports.length > 1) && 
          <div className="mt-6">
            <div 
              className="rounded bg-light-danger bg-hover-danger text-hover-white w-24px" 
              onClick={ () => rowHelpers.remove(idx)}
              title="Delete Transport Detail"
            >
              <KTSVG path='/media/icons/duotune/arrows/arr088.svg' className='svg-icon-1' />
            </div>                
          </div>        
          }              
        </div>
        <div className='separator border-gray-200'></div>
        </Fragment>  
        ))
        }
        </div>
        </>
      )}
    />    
  </div>
  )
}

export {billingDetailsSchema, initBillingDetails, FormikBillingDetails, ParseBillingFields}